import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Plus,
  Pencil,
  Trash2,
  Loader2,
  KeyRound,
  Eye,
  EyeOff,
  Copy,
  ShieldCheck,
  Search,
} from "lucide-react";
import { api, apiError } from "@/lib/api";
import { PageHeader, EmptyState } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const empty = { nama_aplikasi: "", username: "", password: "", pin: "", keterangan: "" };

export default function DirektoriAkun() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [buka, setBuka] = useState({});
  const [cari, setCari] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/direktori");
      setRows(data);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const save = async () => {
    if (!form.nama_aplikasi.trim()) return toast.error("Nama aplikasi wajib diisi");
    setSaving(true);
    try {
      if (editing) await api.put(`/direktori/${editing.id}`, form);
      else await api.post("/direktori", form);
      toast.success(editing ? "Akun diperbarui" : "Akun tersimpan & terenkripsi");
      setOpen(false);
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`Hapus akun ${row.nama_aplikasi}?`)) return;
    try {
      await api.delete(`/direktori/${row.id}`);
      toast.success("Akun dihapus");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const salin = async (teks, label) => {
    try {
      await navigator.clipboard.writeText(teks || "");
      toast.success(`${label} disalin`);
    } catch (e) {
      toast.error("Gagal menyalin");
    }
  };

  const kunci = cari.trim().toLowerCase();
  const daftar = rows.filter(
    (r) =>
      !kunci ||
      (r.nama_aplikasi || "").toLowerCase().includes(kunci) ||
      (r.username || "").toLowerCase().includes(kunci)
  );

  const Rahasia = ({ row, field, label }) => {
    const terlihat = buka[`${row.id}-${field}`];
    const nilai = row[field];
    if (!nilai) return <span className="text-[#4d586f]">-</span>;
    return (
      <div className="flex items-center gap-1.5">
        <span className="font-mono-num text-white">{terlihat ? nilai : "••••••••"}</span>
        <button
          data-testid={`direktori-toggle-${field}-${row.id}`}
          title={terlihat ? "Sembunyikan" : "Tampilkan"}
          onClick={() => setBuka((b) => ({ ...b, [`${row.id}-${field}`]: !terlihat }))}
          className="text-[#4d586f] transition-colors duration-200 hover:text-[#00d09c]"
        >
          {terlihat ? <EyeOff size={13} /> : <Eye size={13} />}
        </button>
        <button
          data-testid={`direktori-copy-${field}-${row.id}`}
          title={`Salin ${label}`}
          onClick={() => salin(nilai, label)}
          className="text-[#4d586f] transition-colors duration-200 hover:text-[#00e5ff]"
        >
          <Copy size={13} />
        </button>
      </div>
    );
  };

  return (
    <div data-testid="direktori-page">
      <PageHeader
        eyebrow="Direktori Akun"
        title="Penyimpanan Akun Pribadi"
        description="Khusus akun Anda sendiri. Password dan PIN dienkripsi AES-256-GCM sebelum masuk database."
      >
        <Button
          data-testid="direktori-add-btn"
          onClick={() => {
            setEditing(null);
            setForm(empty);
            setOpen(true);
          }}
          className="rounded-full bg-[#00d09c] font-semibold text-[#080b11] hover:bg-[#00e5ff]"
        >
          <Plus size={16} className="mr-1.5" /> Tambah Akun
        </Button>
      </PageHeader>

      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="relative sm:w-72">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#4d586f]" />
          <Input
            data-testid="direktori-search-input"
            value={cari}
            onChange={(e) => setCari(e.target.value)}
            placeholder="Cari aplikasi atau username"
            className="h-9 border-[#1e2536] bg-[#10141d] pl-9 text-sm text-white placeholder:text-[#3a4457] focus-visible:ring-[#00d09c]"
          />
        </div>
        <p className="font-mono-num flex items-center gap-1.5 text-xs text-[#4d586f] sm:ml-auto">
          <ShieldCheck size={13} className="text-[#00d09c]" /> Terenkripsi AES-256-GCM ·{" "}
          {daftar.length} akun
        </p>
      </div>

      <div className="overflow-hidden rounded-lg border border-[#1e2536] bg-[#10141d]">
        {loading ? (
          <div className="flex justify-center py-16">
            <Loader2 className="animate-spin text-[#2b3550]" />
          </div>
        ) : daftar.length === 0 ? (
          <EmptyState
            icon={KeyRound}
            title={rows.length === 0 ? "Belum ada akun tersimpan" : "Tidak ada akun yang cocok"}
            hint={
              rows.length === 0
                ? "Klik 'Tambah Akun' untuk menyimpan kredensial aplikasi Anda dengan aman."
                : "Coba ubah kata pencarian."
            }
          />
        ) : (
          <div className="overflow-x-auto">
            <table data-testid="direktori-table" className="w-full min-w-[820px] text-sm">
              <thead>
                <tr className="border-b border-[#1e2536] bg-[#0d1119] text-left text-xs uppercase tracking-wider text-[#4d586f]">
                  <th className="px-4 py-3 font-medium">Nama Aplikasi</th>
                  <th className="px-4 py-3 font-medium">Username</th>
                  <th className="px-4 py-3 font-medium">Password</th>
                  <th className="px-4 py-3 font-medium">PIN</th>
                  <th className="px-4 py-3 font-medium">Keterangan</th>
                  <th className="px-4 py-3 text-right font-medium">Aksi</th>
                </tr>
              </thead>
              <tbody>
                {daftar.map((r) => (
                  <tr
                    key={r.id}
                    data-testid={`direktori-row-${r.nama_aplikasi}`}
                    className="border-b border-[#1a2030] last:border-0 hover:bg-white/[0.03]"
                  >
                    <td className="whitespace-nowrap px-4 py-3 font-semibold text-[#00d09c]">
                      {r.nama_aplikasi}
                    </td>
                    <td className="px-4 py-3 text-white">{r.username || "-"}</td>
                    <td className="px-4 py-3">
                      <Rahasia row={r} field="password" label="Password" />
                    </td>
                    <td className="px-4 py-3">
                      <Rahasia row={r} field="pin" label="PIN" />
                    </td>
                    <td className="max-w-[240px] px-4 py-3 text-[#8f9bb3]">{r.keterangan || "-"}</td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-2">
                        <Button
                          data-testid={`direktori-edit-btn-${r.nama_aplikasi}`}
                          size="icon"
                          variant="outline"
                          onClick={() => {
                            setEditing(r);
                            setForm({ ...empty, ...r });
                            setOpen(true);
                          }}
                          className="h-8 w-8 border-[#1e2536] bg-transparent text-[#8f9bb3] hover:text-[#00d09c]"
                        >
                          <Pencil size={14} />
                        </Button>
                        <Button
                          data-testid={`direktori-delete-btn-${r.nama_aplikasi}`}
                          size="icon"
                          variant="outline"
                          onClick={() => remove(r)}
                          className="h-8 w-8 border-[#1e2536] bg-transparent text-[#8f9bb3] hover:text-[#ff3b30]"
                        >
                          <Trash2 size={14} />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="direktori-dialog" className="border-[#1e2536] bg-[#10141d]">
          <DialogHeader>
            <DialogTitle className="text-white">
              {editing ? `Edit Akun ${editing.nama_aplikasi}` : "Tambah Akun Baru"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {[
              ["nama_aplikasi", "Nama Aplikasi"],
              ["username", "Username"],
              ["password", "Password"],
              ["pin", "PIN"],
            ].map(([key, label]) => (
              <div key={key}>
                <Label className="text-xs uppercase tracking-[0.12em] text-[#8f9bb3]">{label}</Label>
                <Input
                  data-testid={`direktori-input-${key}`}
                  value={form[key] || ""}
                  onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                  className="mt-1.5 border-[#1e2536] bg-[#0d1119] text-white focus-visible:ring-[#00d09c]"
                />
              </div>
            ))}
            <div>
              <Label className="text-xs uppercase tracking-[0.12em] text-[#8f9bb3]">Keterangan</Label>
              <Textarea
                data-testid="direktori-input-keterangan"
                value={form.keterangan || ""}
                onChange={(e) => setForm({ ...form, keterangan: e.target.value })}
                className="mt-1.5 border-[#1e2536] bg-[#0d1119] text-white focus-visible:ring-[#00d09c]"
              />
            </div>
          </div>
          <p className="flex items-center gap-1.5 text-xs text-[#4d586f]">
            <ShieldCheck size={13} className="text-[#00d09c]" /> Password & PIN dienkripsi AES-256-GCM
            dan hanya bisa dibuka oleh akun Anda.
          </p>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setOpen(false)}
              className="border-[#1e2536] bg-transparent text-[#8f9bb3]"
            >
              Batal
            </Button>
            <Button
              data-testid="direktori-save-btn"
              onClick={save}
              disabled={saving}
              className="rounded-full bg-[#00d09c] font-semibold text-[#080b11] hover:bg-[#00e5ff]"
            >
              {saving ? <Loader2 size={16} className="animate-spin" /> : "Simpan"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
